import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { getGanttConfig } from '@constants/config';
import { getGanttGroupById } from '@actions/groups-actions';
import Content from './content';
import Empty from './empty';
import WarningDialog from './warning-dialog';
import './container.scss';

const NoDatesWarningDialog = ({ groupId }) => (
  <WarningDialog
    dialogType={getGanttConfig().warningDialogTypes.noDates}
    groupId={groupId}
    text="There are items in this group with no dates or just one date. These items will not be included in the Gantt chart until dates are provided."
  />
);

const PerformanceWarningDialog = ({ groupId }) => (
  <WarningDialog
    dialogType={getGanttConfig().warningDialogTypes.performance}
    groupId={groupId}
    text="This view will list items multiple times and could make this page load slow."
  />
);

NoDatesWarningDialog.propTypes = {
  groupId: PropTypes.string
};

PerformanceWarningDialog.propTypes = {
  groupId: PropTypes.string
};

const Container = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();

  // Redux state
  const locationType = useSelector(state => state.groups.gantt.filters.locationType);
  const noDatesWarning = useSelector(state => state.groups.gantt.noDatesWarning);
  const performanceThresholdReached = useSelector(state => state.groups.gantt.performanceThresholdReached);
  const rows = useSelector(state => state.groups.gantt.rows);

  const prevLocationRef = useRef(location);
  const prevLocationTypeRef = useRef(locationType);

  const { dataId } = params;

  const initializePage = useCallback(() => {
    if (!!+dataId) {
      const { locationTypes } = getGanttConfig();
      const getBlocks = locationType === locationTypes.byStreetsAndBlocks.id;
      dispatch(getGanttGroupById(dataId, true, getBlocks));
    }
  }, [dataId, locationType, dispatch]);

  // Initial load on mount
  useEffect(() => {
    initializePage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-initialize when location or locationType changes
  useEffect(() => {
    if (!locationType) {
      return;
    }

    const { locationTypes } = getGanttConfig();
    const locationChanged = location !== prevLocationRef.current;
    const locationTypeChanged = locationType !== prevLocationTypeRef.current;

    // Reload when we change the location type,
    // but only if it's "by streets and blocks",
    // since it retrieves the additional block data
    // which takes more time.
    if (locationChanged ||
        (locationTypeChanged && locationType === locationTypes.byStreetsAndBlocks.id)
    ) {
      initializePage();
    }

    prevLocationRef.current = location;
    prevLocationTypeRef.current = locationType;
  }, [location, locationType, initializePage]);

  if (!rows) {
    return null;
  }

  const isRowsEmpty = isEmpty(rows);

  return (
    <div styleName="gantt-container">
      {!isRowsEmpty && <Content />}
      {isRowsEmpty && <Empty />}
      {noDatesWarning && <NoDatesWarningDialog groupId={dataId} />}
      {performanceThresholdReached && <PerformanceWarningDialog groupId={dataId} />}
    </div>
  );
};

export default Container;
